import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import {
  CheckCircle, AlertCircle, Clock, FileText,
  DollarSign, TruckIcon, XCircle, ArrowRight, Upload
} from 'lucide-react';

const STATUS_CONFIG = {
  load_completed: {
    icon: CheckCircle,
    label: 'Load Completed',
    description: 'Load has been delivered successfully',
    color: 'emerald'
  },
  invoice_uploaded: {
    icon: FileText,
    label: 'Invoice Uploaded',
    description: 'Invoice documents have been submitted',
    color: 'emerald'
  },
  charges_approved: {
    icon: CheckCircle,
    label: 'Charges Approved',
    description: 'Invoice charges have been approved',
    color: 'emerald'
  },
  charges_disputed: {
    icon: AlertCircle,
    label: 'Charges Disputed',
    description: 'Invoice charges are under dispute',
    color: 'rose'
  },
  under_review: {
    icon: Clock,
    label: 'Under Review',
    description: 'Dispute is being reviewed',
    color: 'amber'
  },
  awaiting_admin_action: {
    icon: Clock,
    label: 'Awaiting Decision',
    description: 'Pending admin review',
    color: 'amber'
  },
  awaiting_payment: {
    icon: Clock,
    label: 'Awaiting Payment',
    description: 'Payment pending from customer',
    color: 'amber'
  },
  payment_marked: {
    icon: DollarSign,
    label: 'Payment Marked',
    description: 'Payment has been marked by customer',
    color: 'emerald'
  },
  payment_verified: {
    icon: CheckCircle,
    label: 'Payment Verified',
    description: 'Payment has been verified',
    color: 'emerald'
  },
  released_to_carrier: {
    icon: TruckIcon,
    label: 'Released to Carrier',
    description: 'Funds released to carrier',
    color: 'emerald'
  },
  dispute_resolved: {
    icon: CheckCircle,
    label: 'Dispute Resolved',
    description: 'Dispute has been resolved',
    color: 'emerald'
  },
  dispute_rejected: {
    icon: XCircle,
    label: 'Dispute Rejected',
    description: 'Dispute has been rejected',
    color: 'rose'
  }
};

const TIMELINE_PATHS = {
  normal: [
    'load_completed',
    'invoice_uploaded',
    'charges_approved',
    'payment_marked',
    'payment_verified',
    'released_to_carrier'
  ],
  dispute: [
    'charges_disputed',
    'under_review'
    // The final step will be added dynamically based on dispute resolution.
  ]
};

const BillingTimeline = ({ 
  currentStatus, 
  onAction, 
  validTransitions = [],
  statusHistory = []
}) => {
  const userRole = useSelector(state => state.auth.user?.role);
  const actionInProgress = useSelector(state => state.billing.actionInProgress);
  
  const isInDispute = ['charges_disputed', 'under_review', 'dispute_rejected'].includes(currentStatus);
  const wasDisputed = statusHistory.some(history => 
    ['charges_disputed', 'under_review', 'dispute_resolved', 'dispute_rejected'].includes(history.status)
  );

  const handleAction = async (status) => {
    if (actionInProgress) return;
    
    try {
      let result;
      if (status === 'invoice_uploaded') {
        result = onAction('show_upload_modal');
      } else if (status === 'charges_disputed') {
        result = onAction({ 
          action: 'dispute_charges', 
          data: { reason: 'Dispute initiated', details: '' }
        });
      } else {
        const actionMap = {
          'payment_marked': 'mark_payment',
          'payment_verified': 'verify_payment',
          'released_to_carrier': 'release_payment',
          'charges_approved': 'charges_approved'
        };
        const apiAction = actionMap[status] || status;
        result = onAction(apiAction);
      }
      
      await Promise.resolve(result);
    } catch (error) {
      console.error('Action failed:', error);
    }
  };

  const canPerformAction = (status) => {
    const isCarrier = userRole?.startsWith('carrier');
    const isCustomer = userRole?.startsWith('customer');
    const isAdmin = userRole === 'admin';

    switch (status) {
      case 'invoice_uploaded':
        return isCarrier || isAdmin;
      case 'charges_approved':
        return isCarrier || isAdmin;
      case 'charges_disputed':
        return isCarrier || isAdmin;
      case 'under_review':
        return isAdmin;
      case 'payment_marked':
        return isCustomer || isAdmin;
      case 'payment_verified':
        return isAdmin;
      case 'released_to_carrier':
        return isAdmin;
      case 'dispute_rejected':
        return isAdmin;
      default:
        return false;
    }
  };

  const getActionText = (status) => {
    switch (status) {
      case 'invoice_uploaded':
        return 'Upload Invoice';
      case 'charges_approved':
        return 'Approve Charges';
      case 'charges_disputed':
        return 'Dispute Charges';
      case 'under_review':
        return 'Review Dispute';
      case 'payment_marked':
        return 'Mark as Paid';
      case 'payment_verified':
        return 'Verify Payment';
      case 'released_to_carrier':
        return 'Release Payment';
      case 'dispute_rejected':
        return 'Reject Dispute';
      default:
        return 'Proceed';
    }
  };

  const LoadingSpinner = () => (
    <svg 
      className="animate-spin h-4 w-4" 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const renderTimelineItem = (status, index, isDisputePath = false) => {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    const isActive = currentStatus === status || 
                    statusHistory.some(h => h.status === status);
    const isNext = validTransitions.includes(status);
    const canAct = canPerformAction(status);
    // Only show actions if we're not in the dispute path
    const showAction = isNext && canAct && !isDisputePath;

    // Special handling for invoice upload action
    const isInvoiceUpload = status === 'invoice_uploaded';
    
    // Only show approve/dispute buttons when not in dispute path
    const showApproveDisputeButtons = !isDisputePath && 
                                    currentStatus === 'invoice_uploaded' && 
                                    status === 'charges_approved' &&
                                    canAct && 
                                    validTransitions.includes('charges_approved');

    // Show upload button when not in dispute path
    const showUploadButton = !isDisputePath && 
                           isInvoiceUpload && 
                           currentStatus === 'load_completed' && 
                           canAct && 
                           validTransitions.includes('invoice_uploaded');

    // Only show regular action button if we're not showing special buttons and not in dispute path
    const showRegularAction = showAction && 
                            !showApproveDisputeButtons && 
                            !showUploadButton;

    // Update the loading state check to handle all possible action mappings
    const getActionType = (status) => {
      const actionMap = {
        'payment_marked': 'mark_payment',
        'payment_verified': 'verify_payment',
        'released_to_carrier': 'release_payment',
        'charges_approved': 'charges_approved',
        'charges_disputed': 'dispute_charges',
        'invoice_uploaded': 'show_upload_modal'
      };
      return actionMap[status] || status;
    };

    const isLoading = actionInProgress === getActionType(status);

    return (
      <div key={status} className="relative flex-1">
        {/* Connector Line */}
        {index < (isDisputePath ? TIMELINE_PATHS.dispute.length - 1 : TIMELINE_PATHS.normal.length - 1) && (
          <div className={`
            absolute top-5 left-[calc(50%+20px)] right-0 h-[2px]
            ${isActive ? `bg-${config.color}-200` : 'bg-gray-200'}
          `} />
        )}

        <div className="relative flex flex-col items-center min-h-[160px]">
          {/* Status Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`
              w-10 h-10 rounded-full flex items-center justify-center
              ${isActive 
                ? `bg-${config.color}-100 text-${config.color}-600` 
                : 'bg-gray-50 text-gray-400 border-2 border-gray-200'}
              ${isNext ? `ring-2 ring-${config.color}-500 ring-offset-2` : ''}
            `}
          >
            <Icon className="w-5 h-5" />
          </motion.div>

          {/* Label */}
          <div className="mt-3 text-center mb-8">
            <p className={`
              text-sm font-medium
              ${isActive ? 'text-gray-900' : 'text-gray-500'}
            `}>
              {config.label}
            </p>
            <p className="text-xs text-gray-500 mt-1 max-w-[120px]">
              {config.description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2">
            {showUploadButton && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => handleAction('invoice_uploaded')}
                className={`
                  inline-flex items-center justify-center gap-2
                  px-4 py-2 rounded-lg text-sm font-medium
                  shadow-sm transition-all duration-200
                  bg-white text-blue-600 border border-blue-200 
                  hover:bg-blue-50 hover:border-blue-300
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                  min-w-[140px] whitespace-nowrap
                `}
                disabled={!!actionInProgress}
              >
                {actionInProgress === 'show_upload_modal' ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner />
                    <span>Opening...</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    <span>Upload Invoice</span>
                  </>
                )}
              </motion.button>
            )}

            {showRegularAction && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => handleAction(status)}
                disabled={!!actionInProgress}
                className={`
                  inline-flex items-center justify-center gap-2
                  px-4 py-2 rounded-lg text-sm font-medium
                  shadow-sm transition-all duration-200
                  ${isDisputePath 
                    ? 'bg-white text-rose-600 border border-rose-200 hover:bg-rose-50 hover:border-rose-300'
                    : `bg-white text-${config.color}-600 border border-${config.color}-200 hover:bg-${config.color}-50 hover:border-${config.color}-300`
                  }
                  focus:outline-none focus:ring-2 focus:ring-offset-2
                  ${isDisputePath 
                    ? 'focus:ring-rose-500'
                    : `focus:ring-${config.color}-500`
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                  min-w-[140px] whitespace-nowrap
                `}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner />
                    <span>{getActionText(status)}</span>
                  </div>
                ) : (
                  <>
                    <Icon className="w-4 h-4" />
                    <span>{getActionText(status)}</span>
                  </>
                )}
              </motion.button>
            )}

            {showApproveDisputeButtons && (
              <>
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleAction('charges_approved')}
                  className={`
                    inline-flex items-center justify-center gap-2
                    px-4 py-2 rounded-lg text-sm font-medium
                    shadow-sm transition-all duration-200
                    bg-white text-emerald-600 border border-emerald-200 
                    hover:bg-emerald-50 hover:border-emerald-300
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500
                    min-w-[140px] whitespace-nowrap
                  `}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'charges_approved' ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner />
                      <span>Approving...</span>
                    </div>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Approve Charges</span>
                    </>
                  )}
                </motion.button>

                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleAction('charges_disputed')}
                  className={`
                    inline-flex items-center justify-center gap-2
                    px-4 py-2 rounded-lg text-sm font-medium
                    shadow-sm transition-all duration-200
                    bg-white text-rose-600 border border-rose-200 
                    hover:bg-rose-50 hover:border-rose-300
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500
                    min-w-[140px] whitespace-nowrap
                  `}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'dispute_charges' ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner />
                      <span>Disputing...</span>
                    </div>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      <span>Dispute Charges</span>
                    </>
                  )}
                </motion.button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderMainPath = () => {
    return (
      <div className="flex justify-between gap-8 relative">
        {TIMELINE_PATHS.normal.map((status, index) => {
          // Skip awaiting_payment as it's now combined with payment_marked
          if (status === 'awaiting_payment') return null;
          return renderTimelineItem(status, index);
        })}
      </div>
    );
  };

  const renderDisputePath = () => {
    if (!isInDispute && !wasDisputed) return null;

    // Determine the dispute outcome based on history
    const wasRejected = statusHistory.some(history => history.status === 'dispute_rejected');
    const wasResolved = statusHistory.some(history => history.status === 'dispute_resolved');
    
    // Create the dispute path with dynamic final step
    const disputePath = [...TIMELINE_PATHS.dispute];
    
    if (currentStatus === 'under_review') {
      // Show 'awaiting admin action' when under review
      disputePath.push('awaiting_admin_action');
    } else if (wasRejected) {
      // Show rejected if dispute was rejected
      disputePath.push('dispute_rejected');
    } else if (wasResolved) {
      // Show resolved if dispute was resolved
      disputePath.push('dispute_resolved');
    } else {
      // If still in disputed state but not under review, show both possibilities
      disputePath.push(validTransitions.includes('dispute_rejected') ? 'dispute_rejected' : 'dispute_resolved');
    }

    return (
      <div className="relative mt-12 pt-8 border-t border-dashed border-gray-200">
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white px-4 py-1">
          <span className="text-sm font-medium text-gray-500">Dispute Resolution</span>
        </div>
        <div className="grid grid-cols-3 gap-8">
          {disputePath.map((status, index) => 
            renderTimelineItem(status, index, true)
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-12">
        Payment Timeline
      </h3>
      <div className="space-y-8">
        {renderMainPath()}
        {renderDisputePath()}
      </div>
    </div>
  );
};

export default BillingTimeline;